import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ICON_OPTIONS, resolveIcon } from "@/lib/decorativeIcons";

/**
 * Icon picker button that opens a popover grid of `ICON_OPTIONS` to choose from.
 *
 * - `variant="watermark"` (default): large, semi-transparent, tilted 3D icon
 *   meant to fill empty space on a card. Absolutely positioned — place inside a
 *   `relative overflow-hidden` card.
 * - `variant="inline"`: small, normal-opacity icon button suitable for placing
 *   next to other inline controls (e.g. a ChevronRight arrow) or inside forms.
 */
export function DecorativeIconPicker({
  value,
  defaultIconKey = "building",
  onSelect,
  className,
  variant = "watermark",
}: {
  value: string | null | undefined;
  defaultIconKey?: string;
  onSelect: (key: string) => void;
  className?: string;
  variant?: "watermark" | "inline";
}) {
  const src = resolveIcon(value, defaultIconKey);

  return (
    <Popover>
      <PopoverTrigger asChild>
        {variant === "inline" ? (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            title="Choose icon"
            className={cn(
              "flex items-center justify-center h-7 w-7 rounded-md hover:bg-primary/10 transition-colors shrink-0",
              className
            )}
          >
            <img src={src} alt="" className="h-5 w-5 object-contain" />
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            title="Choose icon"
            className={cn(
              "absolute right-12 -bottom-4 h-24 w-24 flex items-center justify-center rounded-full cursor-pointer group/decor z-0",
              className
            )}
          >
            <img
              src={src}
              alt=""
              className="h-full w-full object-contain opacity-20 rotate-12 transition-transform duration-300 group-hover/decor:rotate-6 group-hover/decor:scale-105 group-hover/decor:opacity-40"
            />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-2"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid grid-cols-6 gap-1">
          {Object.entries(ICON_OPTIONS).map(([key, optSrc]) => (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(key)}
              className={cn(
                "flex items-center justify-center h-9 w-9 rounded-md border transition-colors hover:bg-accent",
                (value || defaultIconKey) === key
                  ? "border-primary bg-primary/10"
                  : "border-transparent"
              )}
            >
              <img src={optSrc} alt="" className="h-6 w-6 object-contain" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
